import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Chip, Divider, Typography } from '@mui/material'

import { computeCreativeAdTooltipData } from '../../helpers/creativeAdTooltip'
import { gameIconSx } from '../../helpers/styles'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { CreativeAd } from '../../types'

interface CreativeAdTooltipContentProps {
  ad: CreativeAd
  /**
   * Active month from the URL/navigation context. Used to read the right
   * `monthly` entry (drives the "New this month" chip). `null` disables
   * month-aware features.
   */
  currentMonth: string | null
  /**
   * Largest individual install share in the surrounding scope (same value used
   * to size the Performance bar in the table/card). Lets us show a Performance %
   * that matches the cell behind the tooltip.
   */
  maxInstallShare: number
}

const MAX_TAGS = 5

/**
 * Rich tooltip surfaced when hovering a creative-ad thumbnail. Keeps the
 * scannable hover-friendly layout while answering the three questions UA
 * users actually ask of every thumbnail: *Is it winning? Is it new or
 * proven? What's it doing?*
 *
 * Used by both the table and card-grid views so hovering a thumbnail behaves
 * identically regardless of which view is active.
 */
export const CreativeAdTooltipContent: FC<CreativeAdTooltipContentProps> = ({ ad, currentMonth, maxInstallShare }) => {
  const { t } = useTranslation('ua-insights')
  const { months } = useUAInsightsMeta()
  const { motivationLabel, tagLabel } = useUATaxonomy()

  const data = useMemo(() => computeCreativeAdTooltipData(ad, currentMonth, maxInstallShare, months), [ad, currentMonth, maxInstallShare, months])

  // Mirror the existing thumbnail-only tooltip: when there's literally
  // nothing extra to show, fall back to the motivations-only block to keep
  // visual parity with the previous design.
  const hasExtraContent = data.firstSeenLabel || data.monthsActive > 0 || ad.tags.length > 0

  return (
    <Box sx={{ minWidth: 220, maxWidth: 280 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {ad.gameIcon && <Box component="img" src={ad.gameIcon} alt={ad.gameName} loading="lazy" decoding="async" sx={gameIconSx(32)} />}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={ad.gameName}
          >
            {ad.gameName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`${ad.network.name} · ${t('seconds', { count: ad.videoDurationSeconds })}`}
          </Typography>
        </Box>
      </Box>

      {hasExtraContent && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
          <Divider sx={{ my: 1 }} />
          <MetaRow label={t('col_ad_performance')} value={`${data.performancePct.toFixed(0)}%`} />
          {data.monthsActive > 0 && (
            <MetaRow
              label={t('tooltip_active')}
              value={
                data.firstSeenLabel
                  ? t('tooltip_active_with_first_seen', { count: data.monthsActive, date: data.firstSeenLabel })
                  : t('tooltip_active_value', { count: data.monthsActive })
              }
            />
          )}
          {data.isNewThisMonth && (
            <Box sx={{ mt: 0.25 }}>
              <Chip
                label={t('tooltip_new_this_month')}
                size="small"
                color="success"
                sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
              />
            </Box>
          )}
        </Box>
      )}

      {ad.dominantMotivations.length > 0 && (
        <>
          {hasExtraContent && <Divider sx={{ my: 1 }} />}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            {t('associated_motivations')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {ad.dominantMotivations.map((m) => (
              <Chip key={m.id} label={motivationLabel(m)} size="small" color="primary" />
            ))}
          </Box>
        </>
      )}

      {ad.tags.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            {t('tooltip_top_tags')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {ad.tags.slice(0, MAX_TAGS).map((tag) => (
              <Chip key={tag.id} label={tagLabel(tag)} size="small" variant="outlined" sx={{ height: 22 }} />
            ))}
          </Box>
        </>
      )}
    </Box>
  )
}

interface MetaRowProps {
  label: string
  value: React.ReactNode
}

/**
 * Compact label/value row used by the tooltip. Aligns the value to the right
 * so successive rows scan as a small data table.
 */
const MetaRow: FC<MetaRowProps> = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    {typeof value === 'string' ? (
      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 'bold' }}>
        {value}
      </Typography>
    ) : (
      value
    )}
  </Box>
)
